import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Landmark, Rocket, Globe, ShieldCheck } from 'lucide-react';
import logo from '../assets/logo.png';

const images = [
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1449156001935-d28bc1dc728f?auto=format&fit=crop&q=80"
];

const services = [
  { title: "Banking & insurance", icon: <Landmark size={22} /> },
  { title: "Fintech, e-commerce & start-ups", icon: <Rocket size={22} /> },
  { title: "Global capability centres (GCC)", icon: <Globe size={22} /> },
  { title: "Individuals, business houses & MSMEs", icon: <ShieldCheck size={22} /> },
];

const Hero = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } 
    }
  };

  return (
    <section className="relative min-h-[100svh] md:min-h-0 md:h-[90vh] w-full overflow-hidden flex flex-col items-center justify-center py-24 md:py-0">
      
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${images[index]})` }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-[#0B2E46]/65" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B2E46]/40 via-transparent to-[#0B2E46]" />
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-6xl px-4 md:px-8 flex flex-col items-center"
      >
        
        <motion.div variants={itemVariants} className="text-center mb-10 md:mb-16 mt-6 md:mt-0">
          <div className="flex justify-center mb-8">
            <motion.img 
              src={logo} 
              alt="LCaaS Logo" 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: [0, -15, 0] // Gentle floating effect
              }}
              transition={{
                opacity: { duration: 1 },
                scale: { duration: 1 },
                y: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
              whileHover={{ scale: 1.05 }}
              className="h-24 md:h-40 w-auto object-contain drop-shadow-2xl cursor-pointer" 
            />
          </div>

          {/* Updated text section: removed the nested span for 'as a' to make font uniform */}
          <p className="text-base md:text-2xl text-white font-serif mb-6 drop-shadow-md tracking-wide">
            Legal and Compliance as a Service
          </p>
          
          <div className="h-[2px] w-24 bg-[#78BE20] mx-auto mb-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 w-full pb-16 md:pb-0">
          {services.map((item, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              whileHover="hover"
              className="group relative flex flex-col items-center text-center cursor-pointer overflow-hidden rounded-xl border border-white/20 bg-[#0B2E46]/40 p-6 md:p-8 backdrop-blur-md transition-all duration-500"
            >
              <div className="relative mb-4 md:mb-6">
                <svg className="absolute -inset-4 w-[calc(100%+32px)] h-[calc(100%+32px)] pointer-events-none z-0" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="38"
                    stroke="rgba(120, 190, 32, 0.2)"
                    strokeWidth="1"
                    fill="none"
                  />
                  <motion.circle
                    cx="50"
                    cy="50"
                    r="38"
                    stroke="#78BE20"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="40 200"
                    variants={{
                      hover: { 
                        rotate: 360,
                        opacity: 1,
                        transition: { duration: 2, repeat: Infinity, ease: "linear" } 
                      },
                      initial: { opacity: 0, rotate: 0 }
                    }}
                    initial="initial"
                  />
                </svg>

                <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 group-hover:bg-[#78BE20]/20 transition-all duration-500 z-10">
                   <div className="text-white/60 group-hover:text-[#78BE20] transition-colors">
                     {item.icon}
                   </div>
                </div>
              </div>
              
              <h3 className="text-[14px] md:text-[15px] font-semibold text-white leading-relaxed group-hover:text-white transition-colors">
                {item.title}
              </h3>

              <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#78BE20] transition-all duration-700 group-hover:w-full" />
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-4 md:bottom-8 flex flex-col items-center"
      >
        <div className="w-[1px] h-12 md:h-16 bg-white/30 relative overflow-hidden">
          <motion.div 
            animate={{ y: [-64, 64] }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-transparent via-[#78BE20] to-transparent"
          />
        </div>
      </motion.div>

    </section>
  );
};

export default Hero;